import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

import { recruitNowWebhookQueryKeys } from "./queryKeys";
import type { WebhookRegistration } from "./useClientWebhookRegistrations";

import { instance } from "@/lib/axios/instance";
import useToast from "@/lib/hooks/useToast";

interface ProblemDetails {
  detail?: string;
  title?: string;
  status?: number;
}

export function useReregisterRecruitNowWebhook() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { t } = useTranslation();

  return useMutation<
    WebhookRegistration,
    AxiosError<ProblemDetails>,
    { clientId: string }
  >({
    mutationFn: async ({ clientId }) => {
      const response = await instance.post<WebhookRegistration>(
        `/api/v2/Webhooks/RecruitNow/Clients/${clientId}/Reregister`
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Optimistically update webhook configuration with new values
      queryClient.setQueryData(
        recruitNowWebhookQueryKeys.configuration(variables.clientId),
        {
          webhookId: data.id ?? null,
          webhookSecret: data.secret ?? null,
        }
      );

      // Invalidate registration by ID query with the new registration ID
      if (data?.id) {
        queryClient.invalidateQueries({
          queryKey: recruitNowWebhookQueryKeys.registrationById(variables.clientId, data.id),
          refetchType: "active",
        });
      }

      // Reset infinite query to exclude the new registration ID
      // The new registration will be fetched separately by ID
      queryClient.resetQueries({
        queryKey: recruitNowWebhookQueryKeys.clientRegistrationsInfinite(variables.clientId, data?.id ?? null),
      });

      // Invalidate paginated query as well
      queryClient.invalidateQueries({
        queryKey: recruitNowWebhookQueryKeys.clientRegistrations(variables.clientId),
        refetchType: "active",
      });

      showToast(t("webhooks.recruitnow.registerSuccess"), "success");
    },
    onError: (error) => {
      const errorMessage =
        error.response?.data?.detail || t("webhooks.recruitnow.registerError");
      showToast(errorMessage, "error");
    },
    onSettled: (_, __, variables) => {
      // Invalidate webhook configuration query
      queryClient.invalidateQueries({
        queryKey: recruitNowWebhookQueryKeys.configuration(variables.clientId),
      });

      // Invalidate overview query to ensure it refetches when user navigates back
      queryClient.invalidateQueries({
        queryKey: recruitNowWebhookQueryKeys.overview(),
        refetchType: "all", // Refetch all matching queries, including inactive ones
      });
    },
  });
}
